import { useConfig } from '../context/ConfigContext';
import BrandLogo from './BrandLogo';
import AppFooter from './AppFooter';
import { loadingSpinner } from '../utils/bookingUtils';
import { COURT_NAME, CHECKOUT_PAGE_PHASES } from '../constants/bookingConst';
import '../css/LandingPage.css';
import '../css/CheckoutPage.css';

/**
 * Page frame shared by the checkout tabs (CheckoutPage, OpenPlayCheckoutPage):
 * the club header and footer, the loading spinners, and the "finished" /
 * "no longer active" notices. The page supplies only the checkout form.
 *
 * @param {Object} props
 * @param {string} props.phase - CHECKOUT_PAGE_PHASES value from useCheckoutPageSession.
 * @param {string} props.inactiveMessage - body of the "no longer active" notice.
 * @param {React.ReactNode} props.children - the checkout form, shown while phase is ACTIVE.
 * @returns {JSX.Element}
 */
function CheckoutPageShell({ phase, inactiveMessage, children }) {
  const { config, loading: configLoading } = useConfig();
  const courtName = config?.courtName || COURT_NAME;

  if (configLoading || phase === CHECKOUT_PAGE_PHASES.PROBING || phase === CHECKOUT_PAGE_PHASES.WAITING) {
    return loadingSpinner('Preparing your checkout', config?.logoUrl);
  }

  if (phase === CHECKOUT_PAGE_PHASES.WORKING) {
    return loadingSpinner('Just a moment', config?.logoUrl);
  }

  /**
   * A card with a title, a line of text and a link back to the site.
   * @param {string} title
   * @param {string} message
   * @returns {JSX.Element}
   */
  const renderNotice = (title, message) => (
    <div className="user-info-form">
      <div className="uif-card checkout-page-notice">
        <h2 className="uif-title">{title}</h2>
        <p className="checkout-page-notice-text">{message}</p>
        <a className="checkout-page-link" href="/">Back to {courtName}</a>
      </div>
    </div>
  );

  return (
    <div className="app checkout-page">
      <header className="lp-header">
        <div className="lp-header-inner">
          <div className="lp-brand">
            <BrandLogo src={config?.logoUrl} alt={courtName} className="lp-logo" />
            <span className="lp-brand-name">{courtName}</span>
          </div>
        </div>
      </header>

      <main className="checkout-page-main">
        {phase === CHECKOUT_PAGE_PHASES.ACTIVE && children}
        {phase === CHECKOUT_PAGE_PHASES.ENDED && renderNotice(
          "You're all set",
          'This checkout is finished. You can close this tab and go back to the booking page.',
        )}
        {phase === CHECKOUT_PAGE_PHASES.INACTIVE && renderNotice(
          'This checkout is no longer active',
          inactiveMessage,
        )}
      </main>

      <AppFooter courtName={courtName} />
    </div>
  );
}

export default CheckoutPageShell;
